// Llevar un video del chat al Estudio de Video — ADR-2026-10-11-ir-al-estudio-desde-el-chat.
//
// El agente no puede cambiarle la pantalla a alguien que está leyendo
// (ADR-2026-09-18-tiendanube-en-el-chat): dibuja una tarjeta con el botón y el usuario
// lo toca. Pero a diferencia de las tarjetas de conectar, ésta ESPERA, como las de
// generar: cuando se resuelve, el usuario ya está en el Estudio con el video en la
// timeline, el chat es el mismo, y el agente sigue su turno ahí para preguntar si lo hace
// él o lo guía. Lo puro (qué le vuelve al agente) vive acá; la tarjeta y el hook que
// abre el Estudio están en `ChatPanelV2` y `useAbrirVideoEnEstudio`.

import type { HerramientaChat } from './tipos';
import { textoDeDescripcion, type DescripcionDePantalla } from './puenteDePantalla';
import type { AdjuntoDeMensaje } from '../pages/v2/adjuntosDelMensaje';
import type { VideoParaElEstudio } from './lienzo/herramientas/useAbrirVideoEnEstudio';
import type { MotivoDeNoAbrir } from './lienzo/herramientas/useAbrirVideoEnEstudio';

export const NOMBRE_IR_AL_ESTUDIO = 'ir_al_estudio';

export const HERRAMIENTA_IR_AL_ESTUDIO: HerramientaChat = {
  name: NOMBRE_IR_AL_ESTUDIO,
  description:
    'Muestra en el chat una tarjeta con el botón «Abrir en el Estudio»: lleva un video del chat al Estudio de Video, en un proyecto nuevo con ese video en la timeline. Usala cuando lo que pide se hace mejor como CAPA sobre el video y no adentro de la generación: texto en pantalla, subtítulos, recortar, unir tomas, música, voz en off, o un retoque chico en un clip largo. Antes de mostrarla decile en una línea por qué conviene (el video limpio primero; el texto como capa sale bien escrito y se puede cambiar o sacar). `video` es el nombre de un video adjunto del chat (uno generado acá se agrega con «Agregar» en su barra); `plan`, una línea en español con lo que van a hacer ahí. La tarjeta ESPERA: el botón lo toca él, y recién cuando se resuelve está en el Estudio. Con la respuesta seguís en el mismo chat: preguntale en una línea si querés que lo hagas vos o lo guiás paso a paso; las herramientas del Estudio te llegan con su próximo mensaje, y ahí cargá la skill `estudio-de-video`. Es de los planes Pro y Ultra y de computadora: si no llega, la respuesta lo dice y ofrecés la otra forma. No cuesta créditos.',
  input_schema: {
    type: 'object',
    properties: {
      video: {
        type: 'string',
        description: 'El nombre del video adjunto del chat que se abre en el Estudio, como aparece en los adjuntos.',
      },
      plan: {
        type: 'string',
        description: 'Qué van a hacer ahí, en una línea en español ("sacar los textos pintados y sumar los tuyos como capa").',
      },
    },
    required: ['video', 'plan'],
  },
};

/** El video del chat → lo que `createStudioProjectFromVideo` necesita. Una creación
 *  (bucket `generadas`, path `<empresa>/full/<archivo>`) entra como `fileName`, igual que
 *  desde el lienzo o Creaciones; cualquier otro video sube con su bucket y su path. */
export function videoDelChatParaElEstudio(
  adjunto: Extract<AdjuntoDeMensaje, { kind: 'video' }>,
  companyId: string,
): VideoParaElEstudio {
  const bucket = adjunto.bucket ?? 'video-project-assets';
  const prefijo = `${companyId}/full/`;
  const name = adjunto.name || undefined;
  if (bucket === 'generadas' && adjunto.storagePath.startsWith(prefijo)) {
    return { fileName: adjunto.storagePath.slice(prefijo.length), name };
  }
  return { origen: { bucket, path: adjunto.storagePath }, name };
}

export type SalidaDeIrAlEstudio =
  | { abierto: true; proyecto: string; descripcion: DescripcionDePantalla | null; video: string }
  | { abierto: false; motivo: MotivoDeNoAbrir | 'sin-video'; detalle?: string };

/**
 * Lo que le vuelve al agente cuando la tarjeta se resuelve. `texto` es lo que lee el
 * modelo; `abierto` y `motivo` quedan para la tarjeta y los tests.
 */
export function resultadoDeIrAlEstudio(salida: SalidaDeIrAlEstudio): Record<string, unknown> {
  if (salida.abierto) {
    const lineas = [
      `Se abrió el Estudio de Video con «${salida.video}» en la timeline, en un proyecto nuevo (/editor-video/${salida.proyecto}). El usuario está ahí ahora y este chat sigue siendo el mismo.`,
      'Preguntale en una línea si querés que lo hagas vos o lo guiás paso a paso. Las herramientas del Estudio te llegan con su próximo mensaje: ahí cargá la skill `estudio-de-video` antes de usarlas.',
      salida.descripcion
        ? textoDeDescripcion(salida.descripcion)
        : 'La pantalla todavía no mandó su ficha: con su próximo mensaje la vas a tener.',
    ];
    return { abierto: true, proyecto: salida.proyecto, texto: lineas.join('\n') };
  }
  const texto =
    salida.motivo === 'plan'
      ? 'El plan del usuario no incluye el Estudio de Video (es de Pro y Ultra); le apareció el aviso para mejorar el plan. No se abrió nada. Ofrecele la otra forma: hacerlo con el texto adentro del video, avisando que si queda mal se prueba de la otra manera, o mejorar el plan.'
      : salida.motivo === 'solo-compu'
        ? 'El Estudio de Video sólo corre en computadora y el usuario está en el teléfono. No se abrió nada: decile que lo siga desde la compu, o hacelo de la otra forma.'
        : salida.motivo === 'sin-video'
          ? (salida.detalle ?? 'No hay ningún video adjunto en el chat con ese nombre. No se abrió nada.')
          : salida.motivo === 'cerrado'
            ? 'El usuario cerró el aviso sin abrir el Estudio. No insistas: preguntá qué prefiere.'
            : `No se pudo abrir el Estudio${salida.detalle ? `: ${salida.detalle}` : ''}. Decíselo y proponé probar de nuevo o la otra forma.`;
  return { abierto: false, motivo: salida.motivo, texto };
}
